import { useCallback, useMemo, useState } from "react";
import {
  buildRows,
  decodeCsvBytes,
  parseCsvText,
  type BuiltRows,
  type ColumnMapping,
  type Encoding,
  type ParsedCsv,
} from "./parseCsv.js";

/**
 * State behind the upload step: the pasted or uploaded text, its parse, the
 * column mapping (guessed, then editable) and the chosen roles. Kept in a hook
 * so the tab shell owns it — the scan needs the rows and roles, the results
 * step needs the original cells for skipped.csv — while UploadStep stays a
 * presentational component.
 */

/**
 * The wildcard role. Exclusive (migration-plan.md §4.5): the API accepts
 * `roles: ["account_owner"]` alone and rejects it mixed with anything else, so
 * ticking it replaces the whole selection and the UI greys the others out.
 */
export const ACCOUNT_OWNER = "account_owner";

export type CsvUpload = {
  text: string;
  setText: (text: string) => void;
  fileName: string | null;
  encoding: Encoding | null;
  parsed: ParsedCsv | null;
  mapping: ColumnMapping;
  setMapping: (mapping: ColumnMapping) => void;
  roles: string[];
  /** Ticks or unticks one role, keeping `account_owner` exclusive. */
  toggleRole: (name: string, checked: boolean) => void;
  /** True when the selection is exactly `["account_owner"]`. */
  ownerMode: boolean;
  built: BuiltRows;
  /** Parses the pasted text. */
  parseText: (text: string) => void;
  /** Decodes and parses an uploaded file; resolves once the state is set. */
  loadFile: (file: File) => Promise<void>;
  readError: string | null;
  /** Everything the Scan button needs: mapped email, a role, a valid row, no file error. */
  canScan: boolean;
};

const EMPTY_MAPPING: ColumnMapping = { email: null, firmRef: null };

export function useCsvUpload(): CsvUpload {
  const [text, setText] = useState("");
  const [fileName, setFileName] = useState<string | null>(null);
  const [encoding, setEncoding] = useState<Encoding | null>(null);
  const [parsed, setParsed] = useState<ParsedCsv | null>(null);
  const [mapping, setMapping] = useState<ColumnMapping>(EMPTY_MAPPING);
  // No role is preselected: the operator must choose the set for every run.
  const [roles, setRoles] = useState<string[]>([]);
  const [readError, setReadError] = useState<string | null>(null);

  const toggleRole = useCallback((name: string, checked: boolean) => {
    setRoles(current => {
      if (!checked) {
        return current.filter(role => role !== name);
      }
      // Owner replaces everything; anything else replaces an owner selection.
      if (name === ACCOUNT_OWNER) {
        return [ACCOUNT_OWNER];
      }
      return [...current.filter(role => role !== ACCOUNT_OWNER), name];
    });
  }, []);

  const parse = useCallback((csv: string) => {
    const next = parseCsvText(csv);
    setParsed(next);
    setMapping(next.guess);
  }, []);

  const parseText = useCallback(
    (csv: string) => {
      setReadError(null);
      setEncoding(null);
      setFileName(null);
      setText(csv);
      parse(csv);
    },
    [parse],
  );

  const loadFile = useCallback(
    async (file: File) => {
      setReadError(null);
      let decoded: { text: string; encoding: Encoding };
      try {
        decoded = decodeCsvBytes(new Uint8Array(await file.arrayBuffer()));
      } catch (error) {
        setReadError(error instanceof Error ? error.message : String(error));
        return;
      }
      setFileName(file.name);
      setEncoding(decoded.encoding);
      setText(decoded.text);
      parse(decoded.text);
    },
    [parse],
  );

  const built = useMemo<BuiltRows>(
    () => (parsed ? buildRows(parsed, mapping) : { rows: [], problems: [] }),
    [parsed, mapping],
  );

  const ownerMode = roles.length === 1 && roles[0] === ACCOUNT_OWNER;

  const canScan =
    parsed !== null &&
    parsed.errors.length === 0 &&
    mapping.email !== null &&
    roles.length > 0 &&
    built.rows.length > 0;

  return {
    text,
    setText,
    fileName,
    encoding,
    parsed,
    mapping,
    setMapping,
    roles,
    toggleRole,
    ownerMode,
    built,
    parseText,
    loadFile,
    readError,
    canScan,
  };
}
